/** Phase 7 — Vanti AI chat panel styling tokens. */

import { button } from '../ui/uiTheme';

export const panelShell =
  'relative flex h-full flex-col border-l border-dark-border bg-dark-surface shadow-2xl shadow-black/60';
export const panelOverlay = 'absolute inset-0 bg-black/50';
export const panelResizeHandle =
  'absolute left-0 top-0 h-full w-1.5 cursor-col-resize select-none [touch-action:none] transition-colors hover:bg-brand-purple/30';

export const chatHeader = 'shrink-0 border-b border-dark-border';
export const chatTitleClass = 'font-semibold text-slate-100 flex items-center gap-1.5';
export const chatTitleIconClass = 'text-brand-purple-light';

export const bubbleUser =
  'max-w-[85%] ml-auto rounded-2xl rounded-br-sm border border-brand-purple/30 bg-brand-purple/20 px-4 py-3 text-sm leading-relaxed text-slate-100';
export const bubbleAssistant =
  'max-w-[90%] overflow-hidden rounded-2xl rounded-bl-sm border border-dark-border bg-dark-elevated px-4 py-3 text-sm leading-relaxed text-slate-200';

export const aiAvatarClass =
  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-purple to-brand-teal text-[10px] font-bold text-white';

export const messageTimestampClass = 'mt-1 text-xs text-slate-600';

export const chatInlineCode = 'rounded bg-dark-base px-1.5 py-0.5 font-mono text-xs text-teal-300';
export const chatCodeBlock =
  'my-2 overflow-x-auto rounded-lg border border-dark-border bg-dark-base p-3 font-mono text-sm text-teal-300';
export const chatLinkClass =
  'text-brand-purple-light underline decoration-brand-purple/30 hover:decoration-brand-purple';
export const chatHeadingClass = 'mb-1 mt-3 font-semibold text-slate-100';
export const chatListClass = 'mb-2 space-y-1 pl-4 text-slate-300';

export const indicatorContainer = 'my-3 flex items-center justify-center';
export const indicatorPill =
  'flex items-center gap-2 rounded-full border border-dark-border bg-dark-elevated px-3 py-1.5 text-xs text-slate-400';
export const indicatorIconPurple = 'h-3.5 w-3.5 shrink-0 text-brand-purple-light';
export const indicatorIconTeal = 'h-3.5 w-3.5 shrink-0 text-brand-teal-light';
export const indicatorIconAmber = 'h-3.5 w-3.5 shrink-0 text-amber-400';
export const indicatorIconRed = 'h-3.5 w-3.5 shrink-0 text-red-400';

export const thinkingRow = 'my-1 flex items-center gap-2 px-4 py-2';
export const thinkingText = 'text-xs italic text-slate-500';
export const toolResultChip =
  'inline-flex items-center gap-1 rounded-full border border-teal-700/30 bg-teal-900/20 px-2.5 py-0.5 text-xs text-teal-300';

export const typingDot = 'h-1.5 w-1.5 animate-bounce rounded-full bg-brand-purple-light';

export const ragPillClass =
  'inline-flex cursor-pointer items-center gap-1 rounded-full border border-dark-border bg-dark-base px-2.5 py-0.5 text-xs text-slate-400 transition-colors hover:border-brand-purple hover:text-slate-200';
export const ragFileIconClass = 'h-3 w-3 text-slate-500';

export const followUpChipClass =
  'cursor-pointer rounded-lg border border-dark-border bg-dark-elevated px-3 py-1.5 text-xs text-slate-300 transition-all hover:border-brand-purple hover:bg-brand-purple/10 hover:text-white';

export const inputArea = 'border-t border-dark-border p-3';
export const inputWrapper =
  'flex items-end gap-2 rounded-xl border border-dark-border bg-dark-elevated px-3 py-2 transition-all duration-200 focus-within:border-brand-purple/50 focus-within:ring-1 focus-within:ring-brand-purple/20';
export const inputTextarea =
  'min-h-8 flex-1 resize-none overflow-hidden bg-transparent py-0 text-sm leading-8 text-slate-100 outline-none placeholder:text-slate-500';
export const sendActiveClass =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-purple text-white shadow-lg shadow-brand-purple/30 transition-all hover:bg-purple-700';
export const sendInactiveClass =
  'flex h-8 w-8 shrink-0 cursor-not-allowed items-center justify-center rounded-lg bg-dark-border text-slate-600';
export const stopButtonClass =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-dark-border bg-dark-elevated text-slate-300 transition-all hover:text-white';

export const modePickerContainer =
  'flex items-center gap-1 overflow-x-auto rounded-lg border border-dark-border bg-dark-base p-1';
export const modePillInactive =
  'flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md px-3 py-1.5 text-xs text-slate-400 transition-all duration-150 hover:bg-dark-elevated hover:text-slate-200';
export const modePillActive =
  'flex shrink-0 items-center gap-1.5 rounded-md border border-dark-border-glow bg-dark-elevated px-3 py-1.5 text-xs font-medium text-white shadow-sm';

export const modeIconInactive = 'h-3 w-3 text-slate-500';

export const modeAccentClass = {
  auto: 'text-brand-purple-light',
  quick: 'text-amber-400',
  interactive: 'text-brand-teal-light',
  analyze: 'text-blue-400',
  code: 'text-emerald-400',
  report: 'text-slate-300',
};

export const threadPanelShell =
  'absolute inset-y-0 right-0 z-20 flex w-72 flex-col border-r border-dark-border bg-dark-base';
export const threadItemInactive =
  'mx-2 cursor-pointer rounded-lg px-3 py-2.5 transition-colors hover:bg-dark-elevated';
export const threadItemActive =
  'mx-2 cursor-pointer rounded-lg border border-dark-border-glow bg-dark-elevated px-3 py-2.5';
export const threadTitleClass = 'truncate text-sm font-medium text-slate-200';
export const threadPreviewClass = 'mt-0.5 truncate text-xs text-slate-500';
export const threadDateClass = 'text-xs text-slate-600';
export const threadDividerClass = 'mt-2 px-3 py-1 text-xs uppercase tracking-wider text-slate-600';

export const scopeRowClass = 'flex items-center gap-2 px-1 pb-1';
export const scopeLabelClass = 'text-xs text-slate-500';
export const scopeChipSelected =
  'inline-flex items-center gap-1.5 rounded-full border border-brand-purple/40 bg-dark-elevated px-2.5 py-1 text-xs text-brand-purple-light';
export const scopeChipUnselected =
  'inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-dark-border bg-dark-base px-2.5 py-1 text-xs text-slate-400 transition-all hover:border-dark-border-glow hover:text-slate-200';

export const ghostIconButton = button.icon;

export const menuDropdown =
  'absolute z-20 origin-top-right rounded-xl border border-dark-border bg-dark-surface py-1 shadow-xl shadow-black/50';
